import React from 'react';
import {
  FlexColumn,
  FlexRow,
  HashLength,
  TruncateBox,
} from '@make-software/cspr-design';
import { Tooltip, BodyText } from '@block-explorer/ui';
import { DeployContractPackageResult } from '@block-explorer/entities';
import { HashLink } from '../hash-link/hash-link';
import { buildRouterPath } from '../../router/utils';
import { CONTRACT_PACKAGE_PATH } from '../../router/paths';
import ContractIcon from '../contract-icon/contract-icon';
import { AvatarProps } from '../avatar/avatar';
import { HashFontSize } from '../table-data-account-info/table-data-account-info';
import {
  SHORTENED_CONTRACT_NAME_LENGTH,
  truncateName,
} from '../action-messages/utils';
import TooltipWithExtendedInfo from '../tooltip-with-extended-info/tooltip-with-extended-info';
import { useTranslation } from 'react-i18next';

interface ContractIdentifierWithNameProps {
  hash: string;
  contractPackage: DeployContractPackageResult;
  hashLength?: HashLength;
  avatarSize?: AvatarProps['size'];
  hashFontSize?: HashFontSize;
  loading?: boolean;
}

export const ContractIdentifierWithName = ({
  hash,
  contractPackage,
  avatarSize = 'default',
  hashLength = HashLength.TINY,
  loading,
}: ContractIdentifierWithNameProps) => {
  const { t } = useTranslation();
  const { latest_version_contract_type_id, icon_url, name, contract_name } =
    contractPackage;

  const path = buildRouterPath(CONTRACT_PACKAGE_PATH, { id: hash }, undefined);
  const contractName = name || contract_name;
  const displayContractName =
    contractName && hashLength === HashLength.TINY
      ? truncateName(contractName, SHORTENED_CONTRACT_NAME_LENGTH)
      : contractName;

  return (
    <FlexRow align={'center'} itemsSpacing={12}>
      <ContractIcon
        src={icon_url}
        size={avatarSize}
        contractTypeId={latest_version_contract_type_id}
        loading={loading}
      />
      <TooltipWithExtendedInfo
        extendedLine={{
          title: contractName,
          caption: t('Contract package name'),
        }}
        tooltipCaption={t('Contract package hash')}
        hash={hash}
      >
        <FlexColumn>
          {displayContractName ? (
            <>
              <BodyText size={3} monotype>
                <HashLink
                  hash={hash}
                  href={path}
                  hashLength={hashLength}
                  ariaDescription={'Link to contract package'}
                />
              </BodyText>
              <TruncateBox size={2}>
                <BodyText size={3} variation="darkGray" noWrap>
                  {displayContractName}
                </BodyText>
              </TruncateBox>
            </>
          ) : (
            <BodyText size={3} monotype>
              <HashLink
                href={path}
                hash={hash}
                hashLength={hashLength}
                ariaDescription={'Link to contract package'}
              />
            </BodyText>
          )}
        </FlexColumn>
      </TooltipWithExtendedInfo>
    </FlexRow>
  );
};
